import {rootState} from '@mintdata-wsp/common/state/RootState';
import {VisualComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualComponent';
import {
    IPanModeDesignUIState,
    ISelectionDesignUIState,
    ISurfaceAreaDesignUIState
} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignUIState';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {SelfAlignedBounds} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/ComponentDesc';
import {Page, PageId} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {DnDDropDirection} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/dnd/DnDDropDirection';
import {DnDModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/dnd/DnDModel';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {RotationModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/rotation/RotationModel';
import {SurfaceFileUploadState} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceFileUploadState';
import {OpenDesignState} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {Guideline} from '@mintdata-wsp/wsp/controllers/visualDesigner/workbench/surface/SurfaceGuidelinesController';
import {SurfacePagesOutline} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/pages/SurfacePagesOutline';
import {Bounds} from '@mintdata/mint-kit/api/models/Bounds';
import {CompassDir} from '@mintdata/mint-kit/api/models/CompassDir';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {BoxHostId, BoxId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Point} from '@mintdata/mint-kit/api/models/Point';
import {Position} from '@mintdata/mint-kit/api/models/Position';
import {EditorId} from '@mintdata/mint-kit/api/properties/PropertyEditor';
import {PropertyEditorViewState} from '@mintdata/mint-kit/api/properties/PropertyEditorViewState';
import {PropertyPanelTabId} from '@mintdata/mint-kit/api/properties/PropertyPanelTabId';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {action, computed, IObservableArray, observable} from 'mobx';

export class SurfacePoint
{
    constructor(public readonly x, public readonly y)
    {
    }

    public static from(x:number, y:number):SurfacePoint
    {
        return new SurfacePoint(x, y);
    }

    toCssStyle():any
    {
        let style:any = {};
        style.transform = `translateX(${this.x}px) translateY(${this.y}px)`;

        return style;
    }

    isEqual(other:SurfacePoint):boolean
    {
        return other && other.x === this.x && other.y === this.y;
    }

    makeCopy():SurfacePoint
    {
        return new SurfacePoint(this.x, this.y);
    }

    toString()
    {
        return `${this.x},${this.y}`;
    }
}

export class SurfaceSize
{
    constructor(public readonly width:number,
                public readonly height:number)
    {
    }

    public static from(width:number, height:number):SurfaceSize
    {
        return new SurfaceSize(width, height);
    }

    toCssStyle():any
    {
        return {width: this.width, height: this.height};
    }

    isEqual(other:SurfaceSize):boolean
    {
        return other && other.width === this.width && other.height === this.height;
    }

    makeCopy():SurfaceSize
    {
        return new SurfaceSize(this.width, this.height);
    }

    toString()
    {
        return `${this.width},${this.height}`;
    }
}

export class SurfaceBounds
{
    @observable
    public position:SurfacePoint;

    @observable
    public size:SurfaceSize;

    constructor(initialPosition:SurfacePoint,
                initialSize:SurfaceSize)
    {
        this.position = initialPosition;
        this.size = initialSize;
    }

    public static from(x:number, y:number, width:number, height:number):SurfaceBounds
    {
        return new SurfaceBounds(new SurfacePoint(x, y), new SurfaceSize(width, height));
    }

    public add(otherBounds:SurfaceBounds):SurfaceBounds
    {
        const pos0 = this.position;
        const size0 = this.size;
        const pos1 = otherBounds.position;
        const size1 = otherBounds.size;

        const x = pos0.x + pos1.x;
        const y = pos0.y + pos1.y;
        const w = size0.width + size1.width;
        const h = size0.height + size1.height;

        return SurfaceBounds.from(x, y, w, h);
    }

    toCssStyle():any
    {
        let pos = this.position.toCssStyle();
        let size = this.size.toCssStyle();

        return Object.assign({}, pos, size);
    }

    isEqual(other:SurfaceBounds):boolean
    {
        return other &&
            this.position.isEqual(other.position) &&
            this.size.isEqual(other.size);
    }

    makeCopy():SurfaceBounds
    {
        return new SurfaceBounds(this.position.makeCopy(), this.size.makeCopy());
    }

    toString()
    {
        return `${this.position}->${this.size}`;
    }

    public toJson():SurfaceBounds
    {
        const position = this.position;
        const size = this.size;

        return {
            position:
                {
                    x: position.x,
                    y: position.y
                },
            size: {
                width: size.width,
                height: size.height
            }
        } as any;
    }

    public static fromJson(json:SurfaceBounds):SurfaceBounds
    {
        return new SurfaceBounds(new SurfacePoint(json.position.x, json.position.y), new SurfaceSize(json.size.width, json.size.height));
    }
}

export class BoxMenuContext
{
    constructor(public mouseDownEvent:MouseEvent,
                public box:Box)
    {
    }
}

export enum PropertiesVisiblePopover
{
    None = 'nonePopover',
    Repeater = 'repeaterPopover',
    Loader = 'loaderPopover',
    Events = 'eventsPopover'
}

export class SurfacePropertiesPanelState
{
    @observable
    public propertiesVisiblePopover:PropertiesVisiblePopover = PropertiesVisiblePopover.None;

    @observable
    public selectedTabId:PropertyPanelTabId = PropertyPanelTabId.Default;

    @observable
    public propertyEditorViewStates:Map<EditorId, PropertyEditorViewState> = new Map();
}

export enum SurfaceInteractionMode
{
    Edit = 'Edit',
    Preview = 'Preview',
    ZenPreview = 'ZenPreview'
}

export enum SelectionInteractionMode
{
    None = 'None',
    StartResizing = 'StartResizing',
    Resizing = 'Resizing',
    StartMoving = 'StartMoving',
    Moving = 'Moving'
}

export class SelectionState implements ISelectionDesignUIState
{
    @observable
    public cursorSelectionEnabled:boolean = false;

    @observable
    public selectedBoxesIdsByPage:Map<PageId, Array<BoxId>> = new Map<PageId, Array<BoxId>>();

    @computed
    public get selectedBoxesOnCurrentLayerPage():Array<Box>
    {
        const openDesign:OpenDesignState = rootState.ui.visualDesigner.openDesign;
        const selectedBoxes:Array<Box> = [];

        if( openDesign && openDesign.initialized && openDesign.designState.surface )
        {
            const currentLayerPage:Page = openDesign.designState.surface.currentLayerPage;
            const selectedBoxesIds:BoxId[] = currentLayerPage ? this.selectedBoxesIdsByPage.get(currentLayerPage.id) : [];

            selectedBoxesIds && selectedBoxesIds.forEach((selectedBoxId:BoxId) =>
            {
                const box:Box = currentLayerPage.artboard.allBoxes.get(selectedBoxId);

                if( box )
                    selectedBoxes.push(box);
            });
        }

        return selectedBoxes;
    }

    /**
     * The actual bounds of selectedBoxes is a computed value (see getBoxesBounds method in SurfaceController).
     *
     * These bounds are used for a guidelines/snap computation when moving selected boxes while they shouldn't change
     * position until the snap threshold is reached.
     *
     * The second use of these bounds is for cursor frame selection. Those two uses are mutually exclusive but
     * probably it would better to use another state for the second use.
     */
    @observable
    public bounds:Bounds = null;

    @observable
    public interactionMode:SelectionInteractionMode = SelectionInteractionMode.None;

    @observable
    public interactionDirection:CompassDir = null;

    public toDesignUIState():ISelectionDesignUIState
    {
        let selectedBoxesIdsByPage:any = {};

        this.selectedBoxesIdsByPage.forEach((value:string[], key:string) => selectedBoxesIdsByPage[key] = value);

        return {
            selectedBoxesIdsByPage
        };
    }

    public fromDesignUIState(state:ISelectionDesignUIState)
    {
        const selectedBoxes:Map<string, string[]> = new Map();

        for( const pageId in state.selectedBoxesIdsByPage )
            selectedBoxes.set(pageId, state.selectedBoxesIdsByPage[pageId]);

        this.selectedBoxesIdsByPage = selectedBoxes;
    }
}

export class PanModeState implements IPanModeDesignUIState
{
    @observable
    public isEnabled:boolean = false;

    @observable
    public position:Point = Point.zero();

    public toDesignUIState():IPanModeDesignUIState
    {
        return {
            position: this.position
        };
    }

    public fromDesignUIState(state:IPanModeDesignUIState)
    {
        this.position = state.position;
    }
}

export class SurfaceMovingState
{
    @observable
    dragStartX:number;

    @observable
    dragStartY:number;

    @observable
    dragDeltaX:number;

    @observable
    dragDeltaY:number;

    @observable
    boxDragStartX:number;

    @observable
    boxDragStartY:number;

    @observable
    boxDragDeltaX:number;

    @observable
    boxDragDeltaY:number;
}

export class BoxReparentingState
{
    @observable
    public isEnabled:boolean = false;

    public initialParents:Map<BoxId, Box> = new Map();

    public initialHosts:Map<BoxId, BoxHostId> = new Map();

    public initialPositions:Map<BoxId, Position> = new Map();

    public repeatedBoxOffsetOnEnable:Point = Point.zero();

    public repeatedBoxOffsetOnDisable:Point = Point.zero();
}

export class DrawingComponentState
{
    @observable
    public drawingComponentEnabled:boolean = false;

    @observable
    public drawingComponent:VisualComponent = null;

    @observable
    public drawingBounds:Bounds = null;

    @observable
    public drawingStartedPoint:SurfacePoint = null;
}

export class SurfaceDnDContext
{
    constructor(public readonly mouseEvent:MouseEvent,
                public readonly box:Box,
                public readonly boxUniqueId:GUID,
                public readonly repeater:Repeater,
                public readonly bounds:SelfAlignedBounds,
                public readonly rotation:RotationModel,
                public readonly dndGroupName:string,
                public readonly dnd:DnDModel)
    {
    }
}

export class SurfaceDnDState
{
    @observable
    public dragContext:SurfaceDnDContext = null;

    @observable
    public dropTargetBoxId:BoxId = null;

    @observable
    public dropTargetBoxUniqueId:BoxId = null;

    @observable
    public dropTargetRepeater:Repeater = null;

    @observable
    public dropDirection:DnDDropDirection = null;
}

export class MouseOverRef
{
    constructor(public readonly box:Box,
                public readonly repeater:Repeater,
                public readonly hostId:BoxHostId = null)
    {
    }
}

export class SurfaceAreaState implements ISurfaceAreaDesignUIState
{
    @observable
    public interactionMode:SurfaceInteractionMode = SurfaceInteractionMode.Edit;

    public selectionState:SelectionState = new SelectionState();

    public panModeState:PanModeState = new PanModeState();

    public surfaceMovingState:SurfaceMovingState = new SurfaceMovingState();

    public boxReparentingState:BoxReparentingState = new BoxReparentingState();

    public drawingComponent:DrawingComponentState = new DrawingComponentState();

    @observable
    public mouseOverRef:MouseOverRef = null;

    @observable
    public zoom:number = 1;

    public surfaceAreaDomElement:HTMLElement = null;

    @observable
    public surfaceCopyPasteState:SurfaceCopyPasteState = null;

    @observable
    public surfaceDnDState:SurfaceDnDState = new SurfaceDnDState();

    @observable
    public surfaceFileUploadState:SurfaceFileUploadState = new SurfaceFileUploadState();

    public readonly repeatersCache:Map<string, Repeater> = new Map<string, Repeater>();

    @observable
    public guidelines:IObservableArray<Guideline> = observable.array<Guideline>();

    public toDesignUIState():ISurfaceAreaDesignUIState
    {
        return {
            selectionState: this.selectionState.toDesignUIState(),
            panModeState: this.panModeState.toDesignUIState(),
            zoom: this.zoom
        };
    }

    @action
    public fromDesignUIState(state:ISurfaceAreaDesignUIState):void
    {
        this.selectionState.fromDesignUIState(state.selectionState);
        this.panModeState.fromDesignUIState(state.panModeState);
        this.zoom = state.zoom;
    }
}

export class SurfaceCopyPasteState
{
    constructor(public pageId:PageId,
                public boxesIds:BoxId[])
    {
    }
}

export class SurfaceDragContext
{
    constructor(public mouseDownOnElement:HTMLElement,
                public elementBoundingClientRect:ClientRect,
                public mouseEvent:MouseEvent,
                public componentToDrag:VisualComponent)
    {
    }
}

export class SurfaceDropContext
{
    constructor(public page:Page,
                public parentBox:Box,
                public parentRepeater:Repeater,
                public parentHostId:BoxHostId,
                public point:SurfacePoint,
                public componentToAdd:VisualComponent)
    {
    }
}

export class SurfaceGlassState
{
    @observable
    public dragContext:SurfaceDragContext = null;

    @observable
    public isReparentingEnabled:boolean = false;
}

export enum VisualComponentsSectionName
{
    General = 'General',
    Widgets = 'Widgets',
    Forms = 'Forms',
    AdvancedForms = 'Advanced Forms',
    Navigation = 'Navigation',
    Integration = 'Integration',
    NoGroup = 'No Group'
}

const visualComponentsSections:Map<VisualComponentsSectionName, Array<string>> = new Map<VisualComponentsSectionName, Array<string>>();
visualComponentsSections.set(
    VisualComponentsSectionName.General,
    ['text', 'label', 'paragraph', 'image', 'icon', 'box', 'button', 'tooltipdecorator', 'popovercontainer', 'progressbar']
);
visualComponentsSections.set(
    VisualComponentsSectionName.Widgets,
    ['calendar', 'table', 'chart', 'map', 'heatmap', 'soundplayer', 'sqleditor', 'jsoneditor', 'videoplayer']
);
visualComponentsSections.set(
    VisualComponentsSectionName.Forms,
    ['textinput', 'dropdown', 'textarea', 'checkbox', 'radiobuttongroup', 'switch', 'timeinput']
);
visualComponentsSections.set(
    VisualComponentsSectionName.AdvancedForms,
    ['numericinput', 'dateinput', 'daterangeinput', 'slider', 'rangeslider', 'buttongroup', 'togglebuttonset', 'richtexteditor', 'filepicker']
);
visualComponentsSections.set(
    VisualComponentsSectionName.Navigation,
    ['navigation', 'tabs', 'splitview']
);
visualComponentsSections.set(
    VisualComponentsSectionName.Integration,
    ['twilio']
);
visualComponentsSections.set(
    VisualComponentsSectionName.NoGroup,
    []
);

const visualComponentsHideInPanel = [
    'label', 'paragraph', 'calendar', 'navigation', 'buttongroup', 'togglebuttonset'
];

export const componentNamesToHideCursorProp = [
    'calendar', 'table', 'chart', 'map', 'soundplayer', 'sqleditor', 'jsoneditor', 'videoplayer', // widgets
    'navigation', 'tabs', 'splitview', // navigation
    'richtexteditor', 'filepicker',
    'tooltipdecorator', 'popovercontainer',

    ...visualComponentsHideInPanel
];

export const getComponentsSectionName = (componentName:string):VisualComponentsSectionName =>
{
    const name = componentName.toLowerCase();

    for( let [sectionName, components] of Array.from(visualComponentsSections.entries()) )
    {
        if( components.indexOf(name) > -1 )
            return sectionName;
    }

    return VisualComponentsSectionName.NoGroup;
};

export const VisualComponentsSectionsOrder:VisualComponentsSectionName[] = [
    VisualComponentsSectionName.General,
    VisualComponentsSectionName.Widgets,
    VisualComponentsSectionName.Forms,
    VisualComponentsSectionName.AdvancedForms,
    VisualComponentsSectionName.Navigation,
    VisualComponentsSectionName.Integration,
    VisualComponentsSectionName.NoGroup
];

export type VisualComponentsSectionId = GUID;

export class VisualComponentsSection
{
    public readonly id:VisualComponentsSectionId = IdUtil.newGUID();
    public readonly sectionName:VisualComponentsSectionName;

    @observable
    public isExpanded:boolean = true;

    @observable
    public components:VisualComponent[] = [];

    constructor(sectionName:VisualComponentsSectionName, length:number)
    {
        this.sectionName = sectionName;
        this.components.length = length;
    }
}

export class SurfaceComponentsPanelState
{
    @observable
    visibleComponents:VisualComponent[] = null;

    @observable
    filterString:string = null;

    @computed
    get visibleComponentsSections():Map<VisualComponentsSectionName, VisualComponentsSection>
    {
        if( this.visibleComponents && this.visibleComponents.length > 0 )
        {
            let VisualComponentsSections:Map<VisualComponentsSectionName, VisualComponentsSection> =
                new Map<VisualComponentsSectionName, VisualComponentsSection>();

            this.visibleComponents.forEach((vc:VisualComponent) =>
            {
                const vcName = vc.name.toLowerCase();

                if( visualComponentsHideInPanel.indexOf(vcName) !== -1 ) return;

                const i:number = visualComponentsSections.get(vc.section).indexOf(vcName);

                if( !VisualComponentsSections.has(vc.section) )
                {
                    const vcSection = new VisualComponentsSection(vc.section, visualComponentsSections.get(vc.section).length);
                    VisualComponentsSections.set(vc.section, vcSection);
                }

                if( vc.section !== VisualComponentsSectionName.NoGroup )
                    VisualComponentsSections.get(vc.section).components[i] = vc;
                else if( vc.name !== 'FakeComponent' )
                    VisualComponentsSections.get(vc.section).components.push(vc);
            });

            return VisualComponentsSections;
        }

        return null;
    }
}

export class PagesState
{
    @observable
    public showRemoveSectionConfirmDialog:boolean = false;

    @observable
    public removeSectionName:string = null;

    public pagesComponentInstance:SurfacePagesOutline = null;
}

export type OutlineItemId = string;

export class OutlineState
{
    @observable
    public openItems:Map<OutlineItemId, boolean> = new Map<OutlineItemId, boolean>();
}

export class SurfacePagesPanelState
{
    public pagesState:PagesState = new PagesState();
    public outlineState:OutlineState = new OutlineState();
}

export class LayoutUpdate
{
    @observable
    public counter:number;

    // TODO
    // @observable
    // public stack:???;

    constructor()
    {
        this.counter = 1;
    }
}

export class SurfaceEditorState
{
    public layoutUpdate:LayoutUpdate = new LayoutUpdate();
    public glass:SurfaceGlassState = new SurfaceGlassState();
    public area:SurfaceAreaState = new SurfaceAreaState();
    public componentsPanel:SurfaceComponentsPanelState = new SurfaceComponentsPanelState();
    public propertiesPanel:SurfacePropertiesPanelState = new SurfacePropertiesPanelState();
    public pagesPanel:SurfacePagesPanelState = new SurfacePagesPanelState();
}
